import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import ElevenYearslogo from "public/eleven-years-logo.svg";
import Halloweenlogo from "public/hallo-logo.svg";
import LiveChatIcon from "public/icons/chat.svg";
import MailBoxIcon from "public/icons/opened-mail-icon.svg";

import {
  GetLeftInfoResponseDTO,
  GetLeftInfoResponseDTOSuccess,
} from "@/api/left-panel/models";
import { Button } from "@/components/form/Button/Button";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { CLOUDFRONT_FONTS_LINK } from "@/constants/links";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { AuthRenderer } from "@/context/auth-renderer/AuthRenderer";
import { useLadyProfileDto } from "@/features/lady-profile/hooks";
import { SweetOrHotBanner } from "@/features/sweet-or-hot-banner/SweetOrHotBanner";
import useChatWindow from "@/hooks/useChatOpen";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { isFeatureFlagEnabled } from "@/lib/feature-flags";
import { getIsNewLeftbarDesign } from "@/lib/feature-flags";
import { WithClassName } from "@/models/utils.models";
import { clsxm } from "@/utils/tailwindMerge";
import { makeUrl } from "@/utils/url-utils/urlMaker";

import { OneTimeOfferBanner } from "../one-time-offer-banner/OneTimeOfferBanner";
import { LeftMenuNav } from "./navigation/LeftMenuNav";
import { ProfileSection } from "./navigation/components/ProfileSection";

type PropTypes = {
  isMainBlockScrolledDown?: boolean;
  handleDrawerClose?: () => void;
  isSidebarVersion?: boolean;
  profileData: GetLeftInfoResponseDTO | null;
};

export const LeftMenu = forwardRef<
  {
    openChatWindow: (type: "liveChat" | "sendRose") => void;
    handleNavigation: (type: string) => void;
  },
  PropTypes & WithClassName
>(
  (
    {
      className,
      isMainBlockScrolledDown,
      handleDrawerClose,
      isSidebarVersion,
      profileData,
    },
    ref,
  ) => {
    const { data: ladyDto } = useLadyProfileDto();
    const openChatWindow = useChatWindow(ladyDto?.forms.id);
    const router = useRouter();
    const handleNavigation = (path: string) => {
      router.push(path);
    };
    useImperativeHandle(ref, () => ({
      openChatWindow: (type: "liveChat" | "sendRose") => openChatWindow(type),
      handleNavigation: (type: string) => handleNavigation(type),
    }));

    const { openModal, removeOne } = useOpenModal();

    const openSignInModal = () =>
      openModal("SIGN_IN_FORM_MODAL", { onClose: removeOne });
    const openRegisterModal = () =>
      openModal("REGISTER_FORM_MODAL", { onClose: removeOne });

    const openInformationModal = (str: string) =>
      openModal("INFORMATION_MODAL", {
        message: str,
        statusReg: "success",
      });
    const withHandleDrawerClose = useCallback(
      (fn?: () => void) => () => {
        handleDrawerClose && handleDrawerClose();
        fn && fn();
      },
      [handleDrawerClose],
    );

    const fullIconPath = useMemo(() => {
      if (isFeatureFlagEnabled("HALLOWEEN_THEME")) {
        return Halloweenlogo;
      }
      if (isFeatureFlagEnabled("ELEVEN_YEARS_THEME")) {
        return ElevenYearslogo;
      }
      return makeUrl(CLOUDFRONT_FONTS_LINK, "img/logo.9fa19e4f.svg");
    }, []);

    const isNewLeftbarDesign = getIsNewLeftbarDesign();

    const authContext = useAuthContext();

    const parsed = GetLeftInfoResponseDTOSuccess.safeParse(profileData);

    return (
      <div
        className={clsxm(
          "w-[260px] p-4 flex flex-col gap-2 pt-0 lg:pt-4",
          className,
        )}
      >
        <div className="flex flex-col gap-2 sm:gap-1">
          <div className="hidden lg:block">
            {/* // TODO: WEB-1726 change to Link */}
            <a
              href={authContext.isAuthenticated ? "/home" : "/"}
              aria-label="Golden Bride - International dating service"
            >
              <Image
                src={fullIconPath}
                alt="Golden Bride Logo"
                width={184}
                height={43}
                loading="eager"
                priority
              />
            </a>
          </div>
          <AuthRenderer>
            <AuthRenderer.IsUnauth>
              {isNewLeftbarDesign ? (
                <div className="grid grid-raw-2 gap-2 mt-2 py-0 lg:pt-3 lg:mt-0 sm:hidden lg:grid">
                  <Button
                    variant="primary"
                    onHoverVariant="color-lighten-green"
                    className="uppercase hidden lg:block"
                    onClick={withHandleDrawerClose(openSignInModal)}
                    aria-label="Sign in"
                  >
                    sign in
                  </Button>
                  <Button
                    variant="default"
                    onHoverVariant="color-lighten-pink"
                    className="uppercase bg-gradient-red-pink-red block sm:hidden lg:block"
                    onClick={withHandleDrawerClose(openRegisterModal)}
                    aria-label="Sign up"
                  >
                    sign up
                  </Button>
                </div>
              ) : (
                <ConditionalRender on={!isSidebarVersion}>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Link
                      href="/home"
                      aria-label="Home page"
                      className="block w-full"
                    >
                      <Button
                        variant="primary"
                        onHoverVariant="color-reverse"
                        className="uppercase w-full"
                        aria-label="Home page"
                      >
                        home
                      </Button>
                    </Link>
                    <Button
                      variant="primary"
                      onHoverVariant="color-reverse"
                      className="uppercase"
                      onClick={withHandleDrawerClose(openSignInModal)}
                      aria-label="Sign in"
                    >
                      sign in
                    </Button>
                  </div>
                </ConditionalRender>
              )}
            </AuthRenderer.IsUnauth>
            <AuthRenderer.IsAuth>
              <ProfileSection profileData={profileData} />

              <div className="lg:grid grid-raw-2 gap-2 mt-2 hidden">
                <Button
                  startIcon={<LiveChatIcon />}
                  variant="primary"
                  startIconClassName={"-ml-7"}
                  size={"large"}
                  className="w-full"
                  onClick={() => openChatWindow("liveChat")}
                  aria-label="Chat with me"
                >
                  live chat
                </Button>
                <Button
                  startIcon={<MailBoxIcon />}
                  startIconClassName={"-ml-7"}
                  variant="secondary"
                  size={"large"}
                  className="w-full"
                  onClick={() => handleNavigation("/mailbox-inbox")}
                  aria-label="Chat with me"
                >
                  <span className="mr-1">mailbox</span>
                  {parsed.success ? (
                    <span
                      className={`absolute right-3 font-normal text-[11px] bg-pink text-white rounded-md ${
                        parsed.data.unreadMail > 9 ? "px-1" : "px-2"
                      }`}
                    >
                      {parsed.data.unreadMail > 100
                        ? "99+"
                        : parsed.data.unreadMail}
                    </span>
                  ) : null}
                </Button>
              </div>
            </AuthRenderer.IsAuth>
          </AuthRenderer>
          <div className="p-2">
            <LeftMenuNav
              isSidebarVersion={isSidebarVersion}
              isMainBlockScrolledDown={isMainBlockScrolledDown}
              withHandleDrawerClose={withHandleDrawerClose}
            />
          </div>
        </div>

        <OneTimeOfferBanner
          isMainBlockScrolledDown={isMainBlockScrolledDown}
          isReplenished={parsed.success && parsed.data.balance.isReplenished}
          onClick={
            authContext.isAuthenticated
              ? () => openInformationModal("Purchase modal here")
              : withHandleDrawerClose(openRegisterModal)
          }
        />
        <ConditionalRender on={!isFeatureFlagEnabled("LEFTBAR_SOH_HIDDEN")}>
          <SweetOrHotBanner withHandleDrawerClose={withHandleDrawerClose} />
        </ConditionalRender>
      </div>
    );
  },
);
LeftMenu.displayName = "LeftMenu";
